import * as React from 'react';

import { enumAssetStatus } from '@rematter/frontend-lib-api-client';
import { Loading } from '@rematter/ui-web';

import {
  useMapAccountAddressesQuery,
  useMapAssetsQuery,
  useMapFacilitiesQuery,
} from '~/modules/transportation/hooks/queries/useMapLocationsQuery';
import { TransportationRootLayout } from '~/modules/transportation/layouts/TransportationRootLayout';
import { MapView as SharedMapView } from '~/shared/components/MapView/MapView';

const LEGEND_ITEMS = [
  { label: 'Facilities', color: '#2768E7' },
  { label: 'Account Addresses', color: '#16A34A' },
];

export const MapView = () => {
  const { data: facilityMarkers = [], isLoading: facilitiesLoading } =
    useMapFacilitiesQuery({});
  const { data: accountMarkers = [], isLoading: accountsLoading } =
    useMapAccountAddressesQuery({});
  const { data: assets = [], isLoading: assetsLoading } = useMapAssetsQuery({});

  const isLoading = facilitiesLoading || accountsLoading || assetsLoading;

  const assetsByLocation = React.useMemo(() => {
    const map = new Map<string, string[]>();
    for (const asset of assets) {
      let markerId: string | null = null;
      if (asset.status === enumAssetStatus.Deployed && asset.accountAddressId) {
        markerId = `account-${asset.accountAddressId}`;
      } else if (
        asset.status === enumAssetStatus.Inventory &&
        asset.facilityId
      ) {
        markerId = `facility-${asset.facilityId}`;
      }
      if (markerId) {
        const existing = map.get(markerId) ?? [];
        existing.push(asset.identifier);
        map.set(markerId, existing);
      }
    }
    for (const [key, identifiers] of map) {
      map.set(key, identifiers.sort());
    }
    return map;
  }, [assets]);

  const markers = React.useMemo(
    () =>
      [...facilityMarkers, ...accountMarkers].map((m) => ({
        ...m,
        assets: assetsByLocation.get(m.id) ?? [],
      })),
    [facilityMarkers, accountMarkers, assetsByLocation],
  );

  return (
    <TransportationRootLayout className="@MapView">
      <div className="relative h-full">
        <Loading isLoading={isLoading}>
          <SharedMapView markers={markers} className="h-full" />
        </Loading>

        <div className="absolute top-3 right-3 rounded-lg bg-white p-3 shadow-md">
          {LEGEND_ITEMS.map((item) => (
            <div key={item.label} className="flex items-center gap-2 text-sm">
              <span
                className="inline-block size-3 rounded-full"
                style={{ backgroundColor: item.color }}
              />
              <span>{item.label}</span>
              <span className="text-muted-foreground">
                (
                {item.color === '#2768E7'
                  ? facilityMarkers.length
                  : accountMarkers.length}
                )
              </span>
            </div>
          ))}
        </div>
      </div>
    </TransportationRootLayout>
  );
};
